"use client";

import React, { useMemo } from "react";
import Image from "next/image";

// 1. Static data moved outside to prevent re-instantiation
const GRID_MAP = [
  [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
  [0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0],
  [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
  [0, 0, 0, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0],
];

const AVATARS = [
  "/images/headshot1.jpg", "/images/headshot2.jpg", "/images/headshot3.jpg",
  "/images/headshot4.jpg", "/images/headshot5.jpg", "/images/headshot6.jpg",
  "/images/headshot7.jpg", "/images/headshot8.jpg",
];

export default function CareersTeam() {
  // 2. Optimization: Memoize the avatar index mapping to ensure stability across renders
  const avatarGrid = useMemo(() => {
    let index = 0;
    return GRID_MAP.map((row) =>
      row.map((cell) => {
        if (cell === 1) {
          const src = AVATARS[index % AVATARS.length];
          index++;
          return src;
        }
        return null;
      })
    );
  }, []);

  const sizeClasses = "w-[6vw] h-[6vw] max-w-[32px] max-h-[32px] md:max-w-[48px] md:max-h-[48px] lg:max-w-[56px] lg:max-h-[56px] rounded-full shrink-0 transition-transform duration-300 hover:scale-110";

  return (
    <section className="py-16 md:py-24 bg-white text-center overflow-hidden" aria-labelledby="team-heading">
      <div className="max-w-7xl mx-auto px-4">
        
        {/* SEO: Semantic heading with clear keyword hierarchy */}
        <h2 id="team-heading" className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-12 max-w-4xl mx-auto leading-tight">
          We’re a global team of analysts, researchers
          <span className="text-gray-400 block md:inline"> and finance professionals helping organizations make smarter decisions.</span>
        </h2>

        {/* Accessibility: Presentation role for the grid as it's purely decorative/visual */}
        <div className="inline-flex flex-col items-center gap-2 md:gap-4 w-full" role="presentation">
          {avatarGrid.map((row, rIndex) => (
            <div 
              key={`row-${rIndex}`} 
              className="flex gap-1.5 md:gap-4 justify-center items-center w-full"
            >
              {row.map((avatarSrc, i) => {
                if (avatarSrc) {
                  return (
                    <div key={`avatar-${rIndex}-${i}`} className={`relative overflow-hidden shadow-sm ring-1 ring-gray-100 ${sizeClasses}`}>
                      <Image
                        src={avatarSrc}
                        alt="" // Leave empty for decorative images to avoid screen reader clutter
                        fill
                        sizes="(max-width: 768px) 32px, (max-width: 1024px) 48px, 56px"
                        className="object-cover"
                        loading="lazy"
                      />
                    </div>
                  );
                }

                return (
                  <div
                    key={`dot-${rIndex}-${i}`}
                    className={`bg-gray-100/50 ${sizeClasses}`}
                    aria-hidden="true"
                  />
                );
              })}
            </div>
          ))}
        </div>

        {/* Quote Section */}
        <figure className="mt-20 max-w-3xl mx-auto px-4">
          <blockquote className="text-xl md:text-3xl text-gray-800 italic font-medium leading-relaxed">
            "Great decisions are built on disciplined research, accurate financial insight, and a team that understands the details behind the numbers."
          </blockquote>
          <figcaption className="mt-6 text-gray-500 font-semibold tracking-wide uppercase text-xs md:text-sm">
            — Leadership Team, Seternity Solutions
          </figcaption>
        </figure>

      </div>
    </section>
  );
}